import { type ReactNode, useState } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import {
  Skeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'
import styles from './IntakeAnswers.module.css'

export interface IntakeAnswersSection {
  title: string | null
  count: number
  answers: ReactNode
}

export interface IntakeAnswersProps {
  sections: IntakeAnswersSection[]
}

export function IntakeAnswers({ sections }: IntakeAnswersProps) {
  const [closed, setClosed] = useState<Record<number, boolean>>({})

  if (sections.length === 0) {
    return <div className={styles.empty}>No intake answers on this job.</div>
  }

  return (
    <div className={styles.root}>
      {sections.map((section, index) => {
        const isOpen = !closed[index]
        return (
          <div className={styles.section} key={index}>
            {section.title && (
              <RawButton
                className={cx(styles.sectionHead, isOpen && styles.open)}
                aria-expanded={isOpen}
                onPress={() => setClosed({ ...closed, [index]: isOpen })}
              >
                <Icon
                  name="chevron-down"
                  size={12}
                  className={styles.caret}
                  aria-hidden="true"
                />
                {section.title}
                <span className={styles.count}>{section.count}</span>
              </RawButton>
            )}
            {(isOpen || !section.title) && (
              <div className={styles.answers}>{section.answers}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export interface IntakeAnswersSkeletonProps {
  /**
   * How many answers the job has.
   */
  answerCount: number
}

const maxSkeletons = 12

/**
 * A placeholder for {@link IntakeAnswers} still loading.
 */
export function IntakeAnswersSkeleton({
  answerCount,
}: IntakeAnswersSkeletonProps) {
  return (
    <div className={styles.root} aria-hidden>
      <div className={styles.section}>
        <div className={styles.answers}>
          <SkeletonList
            count={Math.min(Math.max(answerCount, 1), maxSkeletons)}
          >
            <IntakeAnswerSkeleton />
          </SkeletonList>
        </div>
      </div>
    </div>
  )
}

function IntakeAnswerSkeleton() {
  const { varyPercent, index } = useSkeletonListContext()
  return (
    <div className={styles.answer}>
      <div className={styles.question}>
        <Skeleton width={varyPercent(index % 3 === 0 ? 80 : 55)} />
      </div>
      <div className={styles.value}>
        <Skeleton width={varyPercent(index % 3 === 0 ? 45 : 25)} />
      </div>
    </div>
  )
}

export interface IntakeAnswerProps {
  id: string
  question: string
  isAnswered: boolean
  /**
   * The rendered answer, shown only when `isAnswered`.
   */
  value: ReactNode
  /**
   * Offered beside an unanswered question.
   */
  onAskClient?: () => void
}

export function IntakeAnswer({
  id,
  question,
  isAnswered,
  value,
  onAskClient,
}: IntakeAnswerProps) {
  return (
    <div className={styles.answer} data-test={`intake-answer-${id}`}>
      <div className={styles.question}>{question}</div>
      <div className={styles.value}>
        {isAnswered ? value : <span className={styles.none}>Not provided</span>}
      </div>
      {!isAnswered && onAskClient && (
        <RawButton className={styles.ask} onPress={onAskClient}>
          Ask client
        </RawButton>
      )}
    </div>
  )
}

export interface IntakeAnswerBoolValueProps {
  answer?: boolean
}

export function IntakeAnswerBoolValue({ answer }: IntakeAnswerBoolValueProps) {
  return answer ? (
    <span className={styles.yes}>
      <Icon
        name="status-check"
        size={14}
        color="var(--ok)"
        aria-hidden="true"
      />
      Yes
    </span>
  ) : (
    <span className={styles.no}>No</span>
  )
}
